import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { getStorageAdapter } from "../config/storageAdapter";
import { useActiveProfileStore } from "./activeProfileStore";

export type UserRole = "customer" | "vendor" | "admin";

/**
 * Sub-classification of a `role: "vendor"` account.
 *
 * - "single-venue"  — the classic case: one human runs one venue. Their
 *                     auth uid IS their vendor doc id. Default for existing
 *                     users (missing field is treated as "single-venue").
 * - "multi-manager" — a person who manages multiple venues (e.g. a CEO with
 *                     a chain). They have NO serviceProviders doc of their
 *                     own; their account exists only to switch between
 *                     other vendor accounts via the VenuePicker. Set by the
 *                     VendorTypeSelectionScreen during signup.
 *
 * Branches on this flag should be sparse — keep role-based checks unchanged
 * so existing code paths (vendor tabs, leads, quotes, etc.) keep working.
 */
export type VendorType = "single-venue" | "multi-manager";

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  /** Set on vendor accounts during signup. Missing on legacy/customer docs. */
  userType?: VendorType;
  createdAt: string;
  profilePicture?: string;
  // Whether the customer has completed the first-time onboarding wizard.
  // Missing on legacy user docs; treated as `true` so existing users bypass.
  hasCompletedOnboarding?: boolean;
  // Whether a vendor has seen the post-onboarding product tour (/vendor-tour).
  // One-time gate: set true when they finish or skip the tour. Missing/false
  // means show it once, right after onboarding completes. Customer-only Evie
  // tour uses hasCompletedOnboarding instead; this is the vendor equivalent.
  hasSeenVendorTour?: boolean;
  // Test-artifact tag. Auto-set on the user doc when their email matches
  // /\+prodtest/i at signup. Surfaces the TestModeBanner across vendor
  // dashboard / customer event surfaces.
  _isTest?: boolean;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setUser: (user: User) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  markOnboardingComplete: () => void;
  markVendorTourSeen: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,

      setUser: (user) =>
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
        }),

      logout: () => {
        // Clear active team profile selection on this device so a re-login
        // (possibly by a different team member) starts at the picker.
        useActiveProfileStore.getState().clearActiveProfile();
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      setLoading: (loading) => set({ isLoading: loading }),

      markOnboardingComplete: () =>
        set((state) =>
          state.user ? { user: { ...state.user, hasCompletedOnboarding: true } } : state
        ),

      markVendorTourSeen: () =>
        set((state) =>
          state.user ? { user: { ...state.user, hasSeenVendorTour: true } } : state
        ),
    }),
    {
      name: "eventify-auth-storage",
      storage: createJSONStorage(() => getStorageAdapter()),
      onRehydrateStorage: () => (state) => {
        // After rehydration, set loading to false
        if (state) {
          state.isLoading = false;
        }
      },
    }
  )
);
